import {
  differenceInDays,
  endOfMonth,
  format,
  parse,
  startOfMonth,
  subDays,
} from "date-fns"
import { ptBR } from "date-fns/locale"
import { CalendarIcon } from "lucide-react"
import * as React from "react"
import type { DateRange } from "react-day-picker"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/default-calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import useIsMobile from "@/hooks/use-mobile"
import { cn } from "@/lib/utils"

type DateRangePickerProps = SearchParamsType

const YYYY_MM_DD = "yyyy-MM-dd"

const parseDateParam = (dateStr: string): Date =>
  parse(dateStr, YYYY_MM_DD, new Date())

const getDatePresets = (t: (key: string) => string) => [
  {
    label: t("pages.dashboard.dateRangePicker.today"),
    getValue: () => ({
      from: new Date(),
      to: new Date(),
    }),
  },
  {
    label: t("pages.dashboard.dateRangePicker.last7Days"),
    getValue: () => ({
      from: subDays(new Date(), 6),
      to: new Date(),
    }),
  },
  {
    label: t("pages.dashboard.dateRangePicker.last30Days"),
    getValue: () => ({
      from: subDays(new Date(), 29),
      to: new Date(),
    }),
  },
  {
    label: t("pages.dashboard.dateRangePicker.thisMonth"),
    getValue: () => ({
      from: startOfMonth(new Date()),
      to: new Date(),
    }),
  },
  {
    label: t("pages.dashboard.dateRangePicker.lastMonth"),
    getValue: () => {
      const lastMonth = subDays(startOfMonth(new Date()), 1)
      return {
        from: startOfMonth(lastMonth),
        to: endOfMonth(lastMonth),
      }
    },
  },
]

export const DateRangePicker: React.FC<
  DateRangePickerProps
> = ({ searchParams }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const datePresets = React.useMemo(
    () => getDatePresets(t),
    [t]
  )
  const [isOpen, setIsOpen] = React.useState(false)

  const displayDate = React.useMemo<
    DateRange | undefined
  >(() => {
    if (searchParams?.startDate && searchParams?.endDate) {
      return {
        from: parseDateParam(searchParams.startDate),
        to: parseDateParam(searchParams.endDate),
      }
    }
    return datePresets[1].getValue()
  }, [searchParams, datePresets])

  const [tempDate, setTempDate] = React.useState<
    DateRange | undefined
  >(displayDate)
  const [validationError, setValidationError] =
    React.useState<string>("")

  const MAX_DAYS = 30

  const validatePeriod = React.useCallback(
    (range: DateRange | undefined): boolean => {
      if (!range?.from || !range?.to) {
        setValidationError("")
        return true
      }

      const daysDiff = differenceInDays(
        range.to,
        range.from
      )

      if (daysDiff > MAX_DAYS) {
        setValidationError(
          t(
            "pages.dashboard.dateRangePicker.maxPeriodError"
          )
        )
        return false
      }

      setValidationError("")
      return true
    },
    [t]
  )

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (open) {
      setTempDate(displayDate)
      setValidationError("")
    }
  }

  const handleSelect = (range: DateRange | undefined) => {
    setTempDate(range)
    validatePeriod(range)
  }

  const handlePresetClick = (
    preset: ReturnType<typeof getDatePresets>[0]
  ) => {
    const range = preset.getValue()
    setTempDate(range)
    validatePeriod(range)
  }

  const handleApply = () => {
    if (!tempDate?.from || !tempDate?.to) return
    if (!validatePeriod(tempDate)) return
    updateUrl(tempDate)
    setIsOpen(false)
  }

  const handleCancel = () => {
    setTempDate(displayDate)
    setIsOpen(false)
  }

  const updateUrl = (range: DateRange) => {
    const params = new URLSearchParams(
      window.location.search
    )
    params.set("startDate", format(range.from!, YYYY_MM_DD))
    params.set("endDate", format(range.to!, YYYY_MM_DD))
    navigate(`?${params.toString()}`)
  }

  return (
    <div className="flex w-full items-center gap-2 @sm:w-auto">
      <label className="hidden text-sm font-medium text-foreground @sm:block">
        {t("pages.dashboard.dateRangePicker.label")}
      </label>
      <Popover
        open={isOpen}
        onOpenChange={handleOpenChange}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-full justify-start text-left text-xs font-normal @sm:w-auto @sm:text-sm",
              !displayDate && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
            {displayDate?.from ? (
              displayDate.to ? (
                <span className="truncate">
                  {format(displayDate.from, "dd/MM/yy")} -{" "}
                  {format(displayDate.to, "dd/MM/yy")}
                </span>
              ) : (
                format(displayDate.from, "dd/MM/yy")
              )
            ) : (
              <span>
                {t(
                  "pages.dashboard.dateRangePicker.selectPeriod"
                )}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[calc(100vw-2rem)] p-0 sm:w-auto sm:max-w-none"
          align="end"
        >
          <div className="flex max-h-[80vh] flex-col overflow-auto">
            <div className="flex flex-col sm:flex-row">
              <div className="flex flex-col gap-1 border-b p-2 sm:border-b-0 sm:border-r sm:p-3">
                <div className="mb-1 text-xs font-medium text-muted-foreground">
                  {t(
                    "pages.dashboard.dateRangePicker.periods"
                  )}
                </div>
                <div className="grid grid-cols-3 gap-1 sm:flex sm:flex-col">
                  {datePresets.map((preset) => (
                    <Button
                      key={preset.label}
                      variant="ghost"
                      size="sm"
                      className="h-7 shrink-0 justify-center px-2 text-[10px] sm:h-8 sm:justify-start sm:px-3 sm:text-xs"
                      onClick={() =>
                        handlePresetClick(preset)
                      }
                    >
                      {preset.label}
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <Calendar
                  initialFocus
                  mode="range"
                  defaultMonth={tempDate?.from}
                  selected={tempDate}
                  onSelect={handleSelect}
                  numberOfMonths={isMobile ? 1 : 2}
                  locale={ptBR}
                  className="capitalize"
                />
              </div>
            </div>
            <div className="border-t">
              {validationError && (
                <div className="px-3 py-2 text-sm text-destructive">
                  {validationError}
                </div>
              )}
              <div className="flex items-center justify-end gap-2 p-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                >
                  {t(
                    "pages.dashboard.dateRangePicker.cancel"
                  )}
                </Button>
                <Button
                  size="sm"
                  onClick={handleApply}
                  disabled={
                    !tempDate?.from ||
                    !tempDate?.to ||
                    !!validationError
                  }
                >
                  {t(
                    "pages.dashboard.dateRangePicker.apply"
                  )}
                </Button>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
